import { useEffect, useState, useCallback } from 'react';
import API_ENDPOINTS from '../../../apiEndpoints';
import { validateEmailSend } from '../utils/validateEmailSend';
import { showErrorSnackbar, showWarningSnackbar } from '../../../components/snackBar/snakbar';
import { useSelector } from 'react-redux';
import { axiosInstanceForLambda } from '../../../library/Axios';
import { Error404SnackbarComponent } from '../utils/snackBarComponent';
import { MODAL_HEADERS, TYPE_MAPPING } from '../constants/emailConstants';

export interface Recipient {
  id: number;
  recipient: Boolean;
  recipientId: number;
  name?: string;
  email: string;
  invoiceNumber?: string;
  invoiceType?: string;
  isActive?: number;
}

export interface SendEmailHookOptions {
  initialPageType?: string;
  recipients: Recipient[];
  onClose?: () => void;
  onProgress?: (progress: { sentEmails: number; totalEmails: number; message: string; progressPercentage: number }) => void;
  templateContent?: string;
  getTemplateContent?: () => string;
}

interface FinancialYear {
  startdate: number;
  enddate: number;
}

interface UserPreferenceAndReducer {
  financialYear?: FinancialYear;
}

interface RootState {
  userPrefrenceandReducer?: UserPreferenceAndReducer;
}

export type EmailSendResponse = {
  status: string;
  message: string;
  data: {
    summary: {
      total: number;
      success: number;
      failed: number;
    };
    details: {
      email: string;
      party: string;
      status: string;
      message: string;
      vouchernumber?: string;
      recipient?: string;
    }[];
  };
} | null;

export type EmailSendData = NonNullable<EmailSendResponse>['data'];

export function useSendEmail({ initialPageType = '', recipients, onClose, onProgress, templateContent, getTemplateContent }: SendEmailHookOptions = { recipients: [] }) {
  const [loading, setLoading] = useState(false);
  const [pageType, setPageType] = useState(initialPageType);
  const [modalHeader, setModalHeader] = useState('');
  const [dateRange, setDateRange] = useState<{ from: number | null; to: number | null }>({ from: null, to: null });
  const [responseSummary, setResponseSummary] = useState<{ open: boolean; data: EmailSendResponse }>({ open: false, data: null });

  const { startdate, enddate } = useSelector((state: RootState) => state.userPrefrenceandReducer?.financialYear || {}) as FinancialYear;

  useEffect(() => {
    setPageType(initialPageType);
  }, [initialPageType]);

  useEffect(() => {
    setModalHeader(MODAL_HEADERS[pageType] || '');
  }, [pageType]);

  const handleDateRangeChange = useCallback((newRange: { from: number | null; to: number | null }) => {
    setDateRange(newRange);
  }, []);

  const animateProgress = (targetProgress: number, message: string, duration: number, onProgress: (progress: { sentEmails: number; totalEmails: number; message: string; progressPercentage: number }) => void, activeRecipientsLength: number, currentProgress: { value: number }, animationCancelled: { value: boolean }): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      if (animationCancelled.value) {
        reject(new Error('Animation cancelled'));
        return;
      }

      const startTime = Date.now();
      const startProgress = currentProgress.value;

      const updateProgress = () => {
        if (animationCancelled.value) {
          reject(new Error('Animation cancelled'));
          return;
        }

        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = 1 - Math.pow(1 - progress, 3);
        const newProgress = startProgress + (targetProgress - startProgress) * easedProgress;
        currentProgress.value = newProgress;

        onProgress({
          sentEmails: Math.floor((currentProgress.value / 100) * activeRecipientsLength),
          totalEmails: activeRecipientsLength,
          message,
          progressPercentage: currentProgress.value,
        });

        if (progress < 1) {
          requestAnimationFrame(updateProgress);
        } else {
          currentProgress.value = targetProgress;
          resolve();
        }
      };

      requestAnimationFrame(updateProgress);
    });
  };

  const buildEmailPayload = (type: string, ids: Array<string | number>, params?: { startDate?: number; endDate?: number }): { ids: Array<string | number>; startDate?: number; endDate?: number; type: string; emailBody?: string } => {
    return type === 'transaction'
      ? { ids, startDate: params?.startDate || undefined, endDate: params?.endDate || undefined, type }
      : { ids, type };
  };

  const createErrorResponse = (message: string): EmailSendResponse => ({
    status: 'error',
    message,
    data: {
      summary: { total: 1, success: 0, failed: 1 },
      details: [{ email: '', party: 'Error', status: 'Failed', message }],
    },
  });

  const normalizeSuccessResponse = (raw: EmailSendResponse): EmailSendResponse => {
    const inner = raw?.data;
    const s = inner?.summary;
    const rawDetails = inner?.details;
    return {
      status: raw?.status ?? 'success',
      message: raw?.message ?? '',
      data: {
        summary: {
          total: s?.total ?? 0,
          success: s?.success ?? 0,
          failed: s?.failed ?? 0,
        },
        details: Array.isArray(rawDetails) ? rawDetails : [],
      },
    };
  };

  const handleSendEmail = useCallback(async () => {
    setLoading(true);

    if (onProgress) {
      onProgress({ sentEmails: 0, totalEmails: 0, message: 'Preparing to send emails...', progressPercentage: 0 });
    }

    const animationCancelled = { value: false };
    const currentProgress = { value: 0 };

    try {
      const activeRecipients = recipients.filter((r) => r.isActive === 1);
      const { valid, error } = validateEmailSend(activeRecipients, pageType, dateRange);

      if (!valid) {
        showErrorSnackbar(error || '', 'right', 'bottom');
        setLoading(false);
        if (onProgress) {
          onProgress({ sentEmails: 0, totalEmails: 0, message: 'Validation failed', progressPercentage: 0 });
        }
        return;
      }

      await animateProgress(15, 'Preparing to send emails...', 800, onProgress || (() => {}), activeRecipients.length, currentProgress, animationCancelled);

      const hasPOSInvoices = activeRecipients.some((r) => r.invoiceType === 'PosInvoice');
      const pageKey = initialPageType?.toLowerCase?.() ?? '';
      const type = hasPOSInvoices ? 'pos' : TYPE_MAPPING[pageKey];

      if (!type) {
        showErrorSnackbar('Could not determine email type for this page. Please try again.', 'right', 'bottom');
        setLoading(false);
        animationCancelled.value = true;
        if (onProgress) {
          onProgress({ sentEmails: 0, totalEmails: 0, message: 'Invalid page type', progressPercentage: 0 });
        }
        return;
      }

      const ids: (string | number)[] = activeRecipients.map((r) => r.id ?? '');

      if (ids.length === 0) {
        showWarningSnackbar('No valid recipient IDs available');
        setLoading(false);
        if (onProgress) {
          onProgress({ sentEmails: 0, totalEmails: 0, message: 'No recipients available', progressPercentage: 0 });
        }
        return;
      }

      await animateProgress(30, 'Connecting to email service...', 600, onProgress || (() => {}), activeRecipients.length, currentProgress, animationCancelled);

      const rawTemplate =
        getTemplateContent != null ? getTemplateContent() : templateContent;
      const latestTemplateContent =
        typeof rawTemplate === 'string' ? rawTemplate : rawTemplate != null ? String(rawTemplate) : '';
      const posRecipients = activeRecipients.filter((r) => r.invoiceType === 'PosInvoice');

      const basePayload = {
        ...buildEmailPayload(type, ids, { startDate: dateRange.from || undefined, endDate: dateRange.to || undefined }),
        ...(latestTemplateContent.length > 0 ? { emailBody: latestTemplateContent } : {}),
      };

      const payload = posRecipients.length > 0
        ? { ...basePayload, posEmails: posRecipients.map((r) => ({ invoiceid: r.id, email: r.email })) }
        : basePayload;

      await animateProgress(45, 'Processing recipients...', 700, onProgress || (() => {}), activeRecipients.length, currentProgress, animationCancelled);
      await animateProgress(60, 'Preparing email data...', 500, onProgress || (() => {}), activeRecipients.length, currentProgress, animationCancelled);

      const apiCallPromise = axiosInstanceForLambda({ url: API_ENDPOINTS.EMAIL_SENDING_URL, data: payload, method: 'POST' });
      const apiProgressPromise = animateProgress(85, 'Sending emails...', 2000, onProgress || (() => {}), activeRecipients.length, currentProgress, animationCancelled);

      let response;
      try {
        const [apiResponse] = await Promise.all([
          apiCallPromise.catch((err) => {
            animationCancelled.value = true;
            throw err;
          }),
          apiProgressPromise.catch(() => {}),
        ]);
        response = apiResponse;
      } catch (error) {
        animationCancelled.value = true;
        throw error;
      }

      const data = response?.data as EmailSendResponse;

      if (data == null || typeof data !== 'object') {
        throw new Error('No response data received from server');
      }

      if (data.status === 'error') {
        if (!data.data && data.message) {
          await animateProgress(100, 'Email sending completed', 300, onProgress || (() => {}), activeRecipients.length, currentProgress, animationCancelled);
          setResponseSummary({ open: true, data: createErrorResponse(String(data.message)) });
          setLoading(false);
          return;
        }
        throw new Error(String(data.message || 'Email sending failed'));
      }

      await animateProgress(100, 'Emails sent successfully!', 800, onProgress || (() => {}), activeRecipients.length, currentProgress, animationCancelled);
      setResponseSummary({ open: true, data: normalizeSuccessResponse(data) });
      setLoading(false);
    } catch (error: any) {
      animationCancelled.value = true;

      if (onProgress) {
        onProgress({ sentEmails: 0, totalEmails: 0, message: 'Error occurred', progressPercentage: 0 });
      }

      if (error?.response) {
        const statusCode = error.response?.status || 500;
        const serverMessage = error.response?.data?.message || error.response?.data?.error;
        const responseData = error.response?.data;

        if (serverMessage && (responseData?.status === 'error' || statusCode === 404 || statusCode === 400)) {
          setResponseSummary({ open: true, data: createErrorResponse(serverMessage) });
          setLoading(false);
          return;
        }

        if (statusCode === 404) {
          Error404SnackbarComponent({ message: serverMessage || 'Resource not found' });
        } else if (statusCode === 400) {
          showErrorSnackbar(serverMessage || 'Invalid request. Please check your input.', 'right', 'bottom');
        } else if (statusCode === 500) {
          showErrorSnackbar(serverMessage || 'Server error occurred. Please try again later.', 'right', 'bottom');
        } else {
          showErrorSnackbar(serverMessage || `Server error (${statusCode})`, 'right', 'bottom');
        }
      } else if (error?.request) {
        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
          showErrorSnackbar('Request timeout. Please check your connection and try again.', 'right', 'bottom');
        } else if (error.message?.includes('Network Error')) {
          showErrorSnackbar('Network error. Please check your internet connection.', 'right', 'bottom');
        } else {
          showErrorSnackbar('Unable to connect to server. Please check your connection.', 'right', 'bottom');
        }
      } else if (error?.message) {
        showErrorSnackbar(error.message, 'right', 'bottom');
      } else {
        showErrorSnackbar('Failed to send email', 'right', 'bottom');
      }

      setLoading(false);
    }
  }, [initialPageType, recipients, dateRange, onProgress, templateContent, getTemplateContent, pageType]);

  const resetAllStates = useCallback(() => {
    setLoading(false);
    setPageType(initialPageType);
    setDateRange({ from: null, to: null });
    if (onProgress) {
      onProgress({ sentEmails: 0, totalEmails: 0, message: '', progressPercentage: 0 });
    }
    onClose?.();
  }, [initialPageType, onClose, onProgress]);

  const handleCloseWrapperModal = useCallback(() => {
    setResponseSummary({ open: false, data: null });
    resetAllStates();
  }, [resetAllStates]);

  const handleCloseResponseSummary = useCallback(() => {
    setResponseSummary((prev) => ({ ...prev, open: false, data: null }));
    resetAllStates();
  }, [resetAllStates]);

  return {
    loading,
    setLoading,
    pageType,
    setPageType,
    dateRange,
    handleDateRangeChange,
    modalHeader,
    handleSendEmail,
    responseSummary,
    handleCloseResponseSummary,
    startdate,
    enddate,
    handleCloseWrapperModal,
  };
}
